import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { Appearance, CssFontSource, CustomFontSource, LayoutObject, loadStripe, SetupIntentResult, Stripe, StripeConstructorOptions, StripeElements, StripeError, StripePaymentElement } from '@stripe/stripe-js';
import APP_UTILITIES from '@/utilities/commonFunctions';
import GlobalMutationNames from '@/store/global/mutationNames';
import MutationNames from './mutationNames';
import store from '@/store';
import { createSetupIntent, submitOrder } from '@/services/stripe/api';
import { getDecodedToken } from '@/services/common/httpClient';
import { Auth0Token } from '@/Model/sessions/types';
import { ApiErrorResponse } from '@/Model/error/api/types';
import { SetupIntentRequest, SetupIntentResponse } from '@/Model/stripe/types';
import {
  SubmitOrderRequest,
  SubmitOrderResponse,
} from '@/Model/checkout/cart/types';
import { Vue } from 'vue-property-decorator';
import APP_CONST from '@/constants/AppConst';

const { SUPRESS_GLOBAL_ERROR_POPUP } = GlobalMutationNames;

const {
  CREATE_SETUP_INTENT_REQUESTED,
  CREATE_SETUP_INTENT_SUCCEEDED,
  CREATE_SETUP_INTENT_FAILED,

  SETUP_STRIPE_REQUESTED,
  SETUP_STRIPE_SUCCEEDED,
  SETUP_STRIPE_FAILED,

  CONFIRM_CARD_REQUESTED,
  CONFIRM_CARD_SUCCEEDED,
  CONFIRM_CARD_FAILED,

  SUBMIT_ORDER_REQUESTED,
  SUBMIT_ORDER_SUCCEEDED,
  SUBMIT_ORDER_FAILED,
  SUBMIT_ORDER_PAYMENT_FAILED,

  SETUP_STRIPE_ELEMENTS_SUCCEEDED,

  RESET_STRIPE_MODULE,
} = MutationNames;

@Module({
  namespaced: true,
  name: 'stripe',
  store,
  dynamic: true,
})
export class StripeModule extends VuexModule {
  stripeAccountId: string = '';
  stripe: Stripe | null = null;
  stripeElements: StripeElements | null = null;
  stripePaymentElement: StripePaymentElement | null = null;

  setupIntentRequest: SetupIntentRequest | null = null;
  setupIntentResponse: SetupIntentResponse | null = null;
  setupIntentResult: SetupIntentResult | null = null;

  isCreatingSetupIntent = false;
  didCreateSetupIntentFail = false;
  createSetupIntentFailedError: ApiErrorResponse | null = null;

  isSettingUpStripe = false;
  didSettingUpStripeFail = false;
  settingUpStripeFailedError: ApiErrorResponse | null = null;

  isConfirmingCard = false;
  didConfirmingCardFail = false;
  confirmCardError: StripeError | null = null;

  submitOrderResponse: SubmitOrderResponse | null = null;
  isSubmittingOrder = false;
  didSubmitOrderFail = false;
  didSubmitOrderPaymentFail = false;
  submitOrderError: ApiErrorResponse | null = null;
  submitOrderPaymentError: string | null = null;

  @Mutation
  [CREATE_SETUP_INTENT_REQUESTED]() {
    this.isCreatingSetupIntent = true;
    this.didCreateSetupIntentFail = false;
    this.createSetupIntentFailedError = null;
  }

  @Mutation
  [CREATE_SETUP_INTENT_SUCCEEDED](setupIntentResponse: SetupIntentResponse) {
    this.setupIntentResponse = setupIntentResponse;
    this.isCreatingSetupIntent = false;
    this.didCreateSetupIntentFail = false;
    this.createSetupIntentFailedError = null;
  }

  @Mutation
  [CREATE_SETUP_INTENT_FAILED](error: ApiErrorResponse) {
    this.isCreatingSetupIntent = false;
    this.didCreateSetupIntentFail = true;
    this.createSetupIntentFailedError = error;
  }

  @Mutation
  [SETUP_STRIPE_REQUESTED]() {
    this.isSettingUpStripe = true;
    this.didSettingUpStripeFail = false;
    this.createSetupIntentFailedError = null;
  }

  @Mutation
  [SETUP_STRIPE_SUCCEEDED](stripe: Stripe) {
    this.stripe = stripe;
    this.isSettingUpStripe = false;
    this.didSettingUpStripeFail = false;
    this.createSetupIntentFailedError = null;
  }

  @Mutation
  [SETUP_STRIPE_FAILED](error: ApiErrorResponse) {
    this.isSettingUpStripe = false;
    this.didSettingUpStripeFail = true;
    this.createSetupIntentFailedError = error;
  }
  @Mutation
  [CONFIRM_CARD_REQUESTED]() {
    this.isConfirmingCard = true;
    this.didConfirmingCardFail = false;
    this.confirmCardError = null;
  }

  @Mutation
  [CONFIRM_CARD_SUCCEEDED](setupIntentResult: SetupIntentResult) {
    this.setupIntentResult = setupIntentResult;
    this.isConfirmingCard = false;
    this.didConfirmingCardFail = false;
    this.confirmCardError = null;
  }

  @Mutation
  [CONFIRM_CARD_FAILED](error: StripeError) {
    this.isConfirmingCard = false;
    this.didConfirmingCardFail = true;
    this.confirmCardError = error;
  }

  @Mutation
  [SUBMIT_ORDER_REQUESTED]() {
    this.isSubmittingOrder = true;
    this.didSubmitOrderFail = false;
    this.submitOrderError = null;
  }

  @Mutation
  [SUBMIT_ORDER_SUCCEEDED](submitOrderResponse: SubmitOrderResponse) {
    this.submitOrderResponse = submitOrderResponse;
    this.isSubmittingOrder = false;
    this.didSubmitOrderFail = false;
    this.submitOrderError = null;
  }

  @Mutation
  [SUBMIT_ORDER_FAILED](error: ApiErrorResponse) {
    this.isSubmittingOrder = false;
    this.didSubmitOrderFail = true;
    this.submitOrderError = error;
  }

  @Mutation
  [SUBMIT_ORDER_PAYMENT_FAILED](error: string) {
    this.isSubmittingOrder = false;
    this.didSubmitOrderFail = true;
    this.didSubmitOrderPaymentFail = true;
    this.submitOrderPaymentError = error;
  }

  @Mutation
  [SETUP_STRIPE_ELEMENTS_SUCCEEDED](stripeElements: StripeElements) {
    this.stripeElements = stripeElements;
    this.stripePaymentElement = stripeElements.getElement('payment');
  }

  @Mutation
  [RESET_STRIPE_MODULE]() {
    this.stripeAccountId = '';
    this.stripe = null;
    this.stripeElements = null;
    this.stripePaymentElement = null;
    this.setupIntentRequest = null;
    this.setupIntentResponse = null;
    this.setupIntentResult = null;
    this.isCreatingSetupIntent = false;
    this.didCreateSetupIntentFail = false;
    this.createSetupIntentFailedError = null;
    this.isSettingUpStripe = false;
    this.didSettingUpStripeFail = false;
    this.settingUpStripeFailedError = null;
    this.isConfirmingCard = false;
    this.didConfirmingCardFail = false;
    this.confirmCardError = null;
  }

  @Action
  async setupStripe() {
    try {
      this.context.commit(`globalModule/${SUPRESS_GLOBAL_ERROR_POPUP}`, true, {
        root: true,
      });

      this.SETUP_STRIPE_REQUESTED();

      const stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISH_KEY, {
        stripeAccount: this.setupIntentResponse!.linkedPaymentAccountId,
      } as StripeConstructorOptions);

      if (stripe) {
        this.SETUP_STRIPE_SUCCEEDED(stripe);
      } else {
        const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(
          'Stripe Setup Failed'
        );
        this.SETUP_STRIPE_FAILED(apiErrorResponse);
      }
    } catch (error) {
      const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(
        error
      );
      this.SETUP_STRIPE_FAILED(apiErrorResponse);
    } finally {
      this.context.commit(`globalModule/${SUPRESS_GLOBAL_ERROR_POPUP}`, false, {
        root: true,
      });
    }
  }

  @Action
  async createSetupIntent() {
    try {
      this.context.commit(`globalModule/${SUPRESS_GLOBAL_ERROR_POPUP}`, true, {
        root: true,
      });

      this.CREATE_SETUP_INTENT_REQUESTED();
      const { userRoles } = (await getDecodedToken()) as Auth0Token;
      const [ { accountId, userId } ] = userRoles;

      const setupIntentRequest: SetupIntentRequest = { userId, accountId };

      const response = await createSetupIntent(setupIntentRequest);
      this.CREATE_SETUP_INTENT_SUCCEEDED(response.data);
      return response;
    } catch (error) {
      const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(
        error
      );
      this.CREATE_SETUP_INTENT_FAILED(apiErrorResponse);
    } finally {
      this.context.commit(`globalModule/${SUPRESS_GLOBAL_ERROR_POPUP}`, false, {
        root: true,
      });
    }
  }

  @Action
  createPaymentMethodElement(elementQuerySelector: string) {
    const clientSecret = this.setupIntentResponse!.clientSecret;
    const appearance: Appearance = {
      theme: 'stripe',
      variables: {
        fontSizeSm: APP_CONST.STYLE.FONT.SIZE.PARAGRAPH_200_LIGHT_SIZE,
        fontFamily: APP_CONST.STYLE.FONT.FAMILY,
        fontWeightNormal: APP_CONST.STYLE.FONT.WEIGHT.HEADLINE_HEAVY_WEIGHT,
        colorDanger: APP_CONST.STYLE.COLOR.DANGER_700,
        colorPrimary: APP_CONST.STYLE.COLOR.NEUTRAL_900
      },
      rules: {
        '.AccordionItem': {
          border: 'none',
          boxShadow: 'none'
        },
        '.Link': {
          textDecoration: 'underline'
        }
      }
    };
    const fonts: Array<CssFontSource | CustomFontSource> = [{
      cssSrc: 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap'
    }];

    const layoutOptions = {
      type: 'accordion',
      defaultCollapsed: true,
      radios: true
    } as LayoutObject;

    const elements = this.stripe!.elements({ clientSecret, appearance, fonts });
    const paymentElement = elements.create('payment', { layout: layoutOptions, wallets: { applePay: 'never', googlePay: 'never' } });
    paymentElement.mount(elementQuerySelector);

    paymentElement.on('ready', () => {
      this.SETUP_STRIPE_ELEMENTS_SUCCEEDED(elements);
    });
  }

  @Action
  async confirmCard() {
    if (this.stripeElements) {
      this.CONFIRM_CARD_REQUESTED();
      const elements = this.stripeElements;
      try {
        const setupIntentResult = await this.stripe!.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        });
        if (setupIntentResult.error) {
          this.CONFIRM_CARD_FAILED(setupIntentResult.error);
        } else {
          this.CONFIRM_CARD_SUCCEEDED(setupIntentResult);
        }
      } catch (error) {
        this.CONFIRM_CARD_FAILED(error as StripeError);
      }
    }
  }

  @Action
  async submitOrder(submitOrderRequest: SubmitOrderRequest) {
    try {
      this.context.commit(`globalModule/${SUPRESS_GLOBAL_ERROR_POPUP}`, true, {
        root: true,
      });
      this.SUBMIT_ORDER_REQUESTED();
      const submitOrderResponse = await submitOrder(submitOrderRequest);
      const failedOrderResponse = submitOrderResponse.data.submitOrderResponseDetails.find(response => response.status === 'failed');
      if (failedOrderResponse) {
        this.SUBMIT_ORDER_PAYMENT_FAILED(failedOrderResponse.failureMessage);
      } else {
        this.SUBMIT_ORDER_SUCCEEDED(submitOrderResponse.data);
      }
      this.context.commit(`globalModule/${SUPRESS_GLOBAL_ERROR_POPUP}`, false, {
        root: true,
      });
    } catch (error) {
      const apiErrorResponse: ApiErrorResponse = APP_UTILITIES.errorToApiErrorResponse(
        error
      );
      this.SUBMIT_ORDER_FAILED(apiErrorResponse);
      this.context.commit(`globalModule/${SUPRESS_GLOBAL_ERROR_POPUP}`, false, {
        root: true,
      });
      Vue.prototype.$eventHub.$emit('showErrorPopup', { name: 'Common_Error' });
    }
  }

  @Action
  reset() {
    this.RESET_STRIPE_MODULE();
  }

  get stripeElementsRendered(): boolean {
    return !!this.stripeElements;
  }
}

export default getModule(StripeModule);
